import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { ChevronDown, Search, Trash2, UserSearch, AudioLines, X, RefreshCw, ArrowLeft } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { auth } from "../components/Authenticator/firebase"
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { useNavigate } from "react-router-dom"
import VoiceAssistantModal from "../components/VoiceAssistantModal"
import { API_URL } from "../config/api"
import CustomerCards from "../components/CustomerCards"
import "../global.css"

const DetalleTarea = () => {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Estados del sidebar
  const [activeSection, setActiveSection] = useState("proyectos")
  const [historialExpanded, setHistorialExpanded] = useState(false)
  const [proyectosExpanded, setProyectosExpanded] = useState(true)

  // Estados del menú de usuario
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [modalView, setModalView] = useState("main")

  // Estado para el modal de búsqueda
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")

  // Estados para asistente de voz
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)

  // Estados para el drag del bottom sheet en móvil
  const [isDragging, setIsDragging] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [startY, setStartY] = useState(0)

  // Estados para el drag del search bottom sheet
  const [isSearchDragging, setIsSearchDragging] = useState(false)
  const [searchDragY, setSearchDragY] = useState(0)
  const [searchStartY, setSearchStartY] = useState(0)

  // Obtener datos del usuario
  const { logout, user } = useAuth()

  // Estados para el perfil de usuario
  const [profileName, setProfileName] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [profileError, setProfileError] = useState("")
  const [profileSuccess, setProfileSuccess] = useState("")
  const fileInputRef = useRef(null)

  // Historial de chats
  const [chatHistory, setChatHistory] = useState([])
  const [historialItemsToShow, setHistorialItemsToShow] = useState(10)
  const HISTORIAL_INCREMENT = 10
  const [timeUpdate, setTimeUpdate] = useState(0)

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Sincronizar datos del usuario
  useEffect(() => {
    if (user) {
      setProfileName(user.displayName || "")
    }
  }, [user])

  // Cargar historial de chats
  const loadChatHistory = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversations?limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Error al cargar el historial: ${response.status}`)
      }

      const data = await response.json()
      if (data.success) {
        const conversations = data.data || []
        setChatHistory(conversations)
      } else {
        throw new Error(data.error || "Error al cargar el historial de chats")
      }
    } catch (error) {
      console.error("Error al cargar el historial de chats:", error)
    }
  }, [])

  useEffect(() => {
    if (user) {
      loadChatHistory()
    }
  }, [loadChatHistory, user])

  // Funciones de usuario
  const getUserInitials = () => {
    if (!user?.displayName) return "U"
    const names = user.displayName.split(" ")
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase()
    }
    return names[0][0].toUpperCase()
  }

  const getUserAvatar = () => {
    if (user?.photoURL) {
      return <img src={user.photoURL} alt="Avatar" className="ds-avatar-image" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
    }
    return <span>{getUserInitials()}</span>
  }


  // Toggle sidebar
  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen)
    } else {
      if (sidebarOpen) {
        setProyectosExpanded(false)
      }
      setSidebarOpen(!sidebarOpen)
    }
  }

  // Funciones del perfil
  const handleImageSelect = () => {
    fileInputRef.current?.click()
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!auth.currentUser) {
      setProfileError("No hay usuario autenticado")
      return
    }

    try {
      setIsUpdatingProfile(true)
      setProfileError("")
      
      const storage = getStorage()
      const storageRef = ref(storage, `avatars/${auth.currentUser.uid}/${file.name}`)
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)
      
      await updateProfile(auth.currentUser, { photoURL: downloadURL })
      setProfileSuccess("Imagen actualizada correctamente")
      setTimeout(() => setProfileSuccess(""), 3000)
    } catch (error) {
      console.error("Error uploading image:", error)
      setProfileError("Error al subir la imagen")
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleProfileUpdate = async () => {
    if (!auth.currentUser) return
    
    try {
      setIsUpdatingProfile(true)
      setProfileError("")
      setProfileSuccess("")

      if (profileName !== user?.displayName) {
        await updateProfile(auth.currentUser, { displayName: profileName })
      }

      if (newPassword && currentPassword) {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword)
        await reauthenticateWithCredential(auth.currentUser, credential)
        await updatePassword(auth.currentUser, newPassword)
        setNewPassword("")
        setCurrentPassword("")
      }

      setProfileSuccess("Perfil actualizado correctamente")
      setTimeout(() => setProfileSuccess(""), 3000)
    } catch (error) {
      console.error("Error updating profile:", error)
      if (error.code === "auth/wrong-password") {
        setProfileError("Contraseña actual incorrecta")
      } else {
        setProfileError("Error al actualizar el perfil")
      }
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  // Eliminar conversación
  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation()
    
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversation/${conversationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        setChatHistory(prev => prev.filter(chat => chat.id !== conversationId))
      }
    } catch (error) {
      console.error("Error deleting conversation:", error)
    }
  }

  // Formatear fecha
  const formatRelativeDate = (date) => {
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffTime / (1000 * 60))

    if (diffMinutes < 1) return "Ahora mismo"
    if (diffMinutes < 60) return `Hace ${diffMinutes} min`
    if (diffHours < 24) return `Hace ${diffHours}h`
    if (diffDays === 0) return "Hoy"
    if (diffDays === 1) return "Ayer"
    if (diffDays < 7) return `Hace ${diffDays} días`
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`
    return date.toLocaleDateString()
  }

  // Función para agrupar chats por fecha
  const groupChatsByDate = useCallback((chats) => {
    const groups = {}

    chats.forEach((chat) => {
      const dateValue = chat.updatedAt || chat.createdAt || chat.created_at || chat.timestamp || chat.date
      if (!dateValue) {
        const groupKey = "Sin fecha"
        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(chat)
        return
      }

      let chatDate
      if (typeof dateValue === 'string') {
        chatDate = new Date(dateValue)
      } else if (typeof dateValue === 'number') {
        chatDate = new Date(dateValue)
      } else if (dateValue.seconds) {
        chatDate = new Date(dateValue.seconds * 1000)
      } else if (dateValue instanceof Date) {
        chatDate = dateValue
      } else {
        chatDate = new Date(dateValue)
      }
      
      if (isNaN(chatDate.getTime())) {
        const groupKey = "Sin fecha"
        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(chat)
        return
      }

      const options = { day: "numeric", month: "long" }
      const groupKey = chatDate.toLocaleDateString("es-ES", options)

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(chat)
    })

    const sortedGroups = {}
    const groupEntries = Object.entries(groups)
    
    groupEntries.sort(([keyA, chatsA], [keyB, chatsB]) => {
      if (keyA === "Sin fecha") return 1
      if (keyB === "Sin fecha") return -1
      
      const getMaxDate = (chats) => {
        return Math.max(...chats.map(chat => {
          const dateValue = chat.updatedAt || chat.createdAt || chat.created_at || chat.timestamp || chat.date
          if (!dateValue) return 0
          
          let date
          if (typeof dateValue === 'string') {
            date = new Date(dateValue)
          } else if (typeof dateValue === 'number') {
            date = new Date(dateValue)
          } else if (dateValue.seconds) {
            date = new Date(dateValue.seconds * 1000)
          } else if (dateValue instanceof Date) {
            date = dateValue
          } else {
            date = new Date(dateValue)
          }
          
          return date && !isNaN(date.getTime()) ? date.getTime() : 0
        }))
      }
      
      const dateA = getMaxDate(chatsA)
      const dateB = getMaxDate(chatsB)
      
      return dateB - dateA
    })
    
    groupEntries.forEach(([key, chats]) => {
      sortedGroups[key] = chats
    })

    return sortedGroups
  }, [])

  const limitedChatHistory = useMemo(() => {
    if (!chatHistory || chatHistory.length === 0) {
      return {
        groups: {},
        hasMore: false,
        remaining: 0
      }
    }

    const sortedHistory = [...chatHistory].sort((a, b) => {
      const getDate = (chat) => {
        const dateValue = chat.updatedAt || chat.createdAt || chat.created_at || chat.timestamp || chat.date
        if (!dateValue) return 0
        
        let date
        if (typeof dateValue === 'string') {
          date = new Date(dateValue)
        } else if (typeof dateValue === 'number') {
          date = new Date(dateValue)
        } else if (dateValue.seconds) {
          date = new Date(dateValue.seconds * 1000)
        } else if (dateValue instanceof Date) {
          date = dateValue
        } else {
          date = new Date(dateValue)
        }
        
        return date && !isNaN(date.getTime()) ? date.getTime() : 0
      }
      
      const dateA = getDate(a)
      const dateB = getDate(b)
      
      return dateB - dateA
    })
    
    const limitedHistory = sortedHistory.slice(0, historialItemsToShow)
    const groupedChats = groupChatsByDate(limitedHistory)

    const total = sortedHistory.length
    const remaining = Math.max(0, total - historialItemsToShow)
    const hasMore = remaining > 0

    return {
      groups: groupedChats,
      hasMore: hasMore,
      remaining: remaining,
      total: total
    }
  }, [chatHistory, historialItemsToShow, groupChatsByDate])

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeUpdate((prev) => prev + 1)
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  // Handlers del bottom sheet
  const handleTouchStart = (e) => {
    setIsDragging(true)
    setStartY(e.touches[0].clientY)
    setDragY(0)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    const currentY = e.touches[0].clientY
    const diff = currentY - startY
    if (diff > 0) setDragY(diff)
  }

  const handleTouchEnd = () => {
    if (dragY > 100) {
      setUserMenuOpen(false)
    }
    setIsDragging(false)
    setDragY(0)
  }

  const handleSearchTouchStart = (e) => {
    setIsSearchDragging(true)
    setSearchStartY(e.touches[0].clientY)
    setSearchDragY(0)
  }

  const handleSearchTouchMove = (e) => {
    if (!isSearchDragging) return
    const currentY = e.touches[0].clientY
    const diff = currentY - searchStartY
    if (diff > 0) setSearchDragY(diff)
  }

  const handleSearchTouchEnd = () => {
    if (searchDragY > 100) {
      setSearchModalOpen(false)
      setSearchQuery("")
    }
    setIsSearchDragging(false)
    setSearchDragY(0)
  }

  // Filtrar historial por búsqueda
  const filteredHistory = debouncedSearchQuery
    ? chatHistory.filter(chat =>
        chat.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      )
    : []

  // Debounce de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  return (
    <div className="ds-home-container">
      {/* Overlay móvil */}
      {isMobile && mobileSidebarOpen && (
        <div className="ds-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* SIDEBAR - Mismo que Inversiones */}
      <div
        className={`ds-sidebar ${
          isMobile
            ? mobileSidebarOpen
              ? "ds-sidebar-mobile-open"
              : "ds-sidebar-mobile-closed"
            : sidebarOpen
              ? "ds-sidebar-expanded"
              : "ds-sidebar-collapsed"
        }`}
      >
        {sidebarOpen || mobileSidebarOpen ? (
          <>
            <div className="ds-sidebar-header">
              <div className="ds-logo-sidebar-container">
                <img src="/logosinnombresinfondo.png" alt="Logo" className="ds-collapsed-logo-img" />
              </div>
              <button className="ds-header-toggle-btn" onClick={toggleSidebar}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v8" />
                  <path d="M20 18h-17" />
                  <path d="M6 15l-3 3l3 3" />
                </svg>
              </button>
              {isMobile && (
                <button className="ds-mobile-close-button" onClick={() => setMobileSidebarOpen(false)}>
                  ×
                </button>
              )}
            </div>

            <div className="ds-sidebar-content">
              <div className="ds-navigation-menu">
                <button className="ds-nav-item" onClick={() => setSearchModalOpen(true)}>
                  <Search size={16} />
                  <span>Buscar</span>
                </button>

                <button 
                  className="ds-nav-item"
                  onClick={() => {
                    navigate("/home")
                    if (isMobile) setMobileSidebarOpen(false)
                  }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                    />
                  </svg>
                  <span>Chat</span>
                </button>
                <button
                  className={`ds-nav-item${proyectosExpanded ? " active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "proyectos") {
                      setActiveSection("proyectos")
                      setProyectosExpanded(true)
                    } else {
                      setProyectosExpanded(!proyectosExpanded)
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>Proyectos</span>
                  <ChevronDown
                    size={16}
                    className={`ds-chevron ${proyectosExpanded ? "expanded" : ""}`}
                    style={{ marginLeft: "auto", transition: "transform 0.2s" }}
                  />
                </button>

                {proyectosExpanded && (
                  <div className="ds-proyectos-content">
                    <button className="ds-nav-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        <line x1="12" y1="11" x2="12" y2="17" />
                        <line x1="9" y1="14" x2="15" y2="14" />
                      </svg>
                      <span>Nuevo proyecto</span>
                    </button>
                    <button 
                      className="ds-nav-item"
                      onClick={() => {
                        navigate("/inversiones")
                        if (isMobile) setMobileSidebarOpen(false)
                      }}
                      style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v12M9 9h6M9 15h6" stroke="#10b981" />
                      </svg>
                      <span style={{ color: '#10b981', fontWeight: 500 }}>Inversiones</span>
                    </button>
                  </div>
                )}

                <button
                  className="ds-nav-item"
                  onClick={() => {
                    navigate("/artefactos")
                    if (isMobile) setMobileSidebarOpen(false)
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  <span>Artefactos</span>
                </button>

                <button
                  className={`ds-nav-item${historialExpanded ? " active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "historial") {
                      setActiveSection("historial")
                      setHistorialExpanded(true)
                      setHistorialItemsToShow(10)
                    } else {
                      setHistorialExpanded(!historialExpanded)
                      if (!historialExpanded) {
                        setHistorialItemsToShow(10)
                      }
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                  <span>Historial</span>
                </button>
              </div>

              {activeSection === "historial" && (
                <div className="ds-section-content">
                  {historialExpanded && (
                    <div className="ds-historial-content">
                      {(() => {
                        const { groups, hasMore, remaining } = limitedChatHistory
                        return (
                          <>
                            {Object.keys(groups).length === 0 ? (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                                <p>No hay conversaciones</p>
                              </div>
                            ) : (
                              Object.entries(groups).map(([dateGroup, chats]) => (
                                <div key={dateGroup} className="ds-date-group">
                                  <div className="ds-date-header">{dateGroup}</div>
                                  {chats.map((chat) => (
                                    <div
                                      key={`${chat.id}-${timeUpdate}`}
                                      className="ds-historial-item"
                                      onClick={() => {
                                        navigate(`/home?conversation=${chat.id}`)
                                        if (isMobile) setMobileSidebarOpen(false)
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <span className="ds-historial-title">
                                        {chat.title === "NUEVA_CONEXION" ? "Nueva conversación" : chat.title}
                                      </span>
                                      <button
                                        className="ds-historial-delete-button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          deleteConversation(chat.id, e)
                                        }}
                                        title="Eliminar chat"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ))
                            )}
                            
                            {hasMore && remaining > 0 && (
                              <div className="ds-show-more-container">
                                <button 
                                  className="ds-show-more-button"
                                  onClick={() => setHistorialItemsToShow(prev => prev + HISTORIAL_INCREMENT)}
                                >
                                  Ver más ({remaining} restantes)
                                </button>
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ds-sidebar-footer">
              <div className="ds-user-section">
                <div className="ds-user-info">
                  <button className="ds-user-circle" onClick={() => {
                    setUserMenuOpen(!userMenuOpen)
                    if (isMobile) {
                      setMobileSidebarOpen(false)
                    }
                  }}>
                    {getUserAvatar()}
                  </button>
                  <div className="ds-user-details">
                    <div className="ds-user-name">{user?.displayName || "Usuario"}</div>
                    <div className="ds-user-email">{user?.email || ""}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="ds-sidebar-collapsed-content">
            <div className="ds-sidebar-header">
              <div className="ds-logo-hover-container" onClick={toggleSidebar}>
                <img src="/logosinnombresinfondo.png" alt="Logo" className="ds-collapsed-logo-img ds-logo-default" />
                <svg
                  className="ds-logo-hover-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M20 12v-6a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v8" />
                  <path d="M4 18h17" />
                  <path d="M18 15l3 3l-3 3" />
                </svg>
              </div>
            </div>

            <div className="ds-sidebar-content">
              <div className="ds-navigation-menu">
                <button className="ds-nav-item" onClick={() => setSearchModalOpen(true)}>
                  <Search size={16} />
                </button>

                <button
                  className="ds-nav-item"
                  onClick={() => {
                    navigate("/home")
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                    />
                  </svg>
                </button>

                <button
                  className={`ds-nav-item ${activeSection === "proyectos" ? "active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "proyectos") {
                      setActiveSection("proyectos")
                      setProyectosExpanded(true)
                    } else {
                      setProyectosExpanded(!proyectosExpanded)
                    }
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </button>

                <button
                  className="ds-nav-item"
                  onClick={() => {
                    navigate("/artefactos")
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </button>

                <button
                  className={`ds-nav-item ${activeSection === "historial" ? "active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "historial") {
                      setActiveSection("historial")
                      setHistorialExpanded(true)
                      setHistorialItemsToShow(10)
                    } else {
                      setHistorialExpanded(!historialExpanded)
                      if (!historialExpanded) {
                        setHistorialItemsToShow(10)
                      }
                    }
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="ds-sidebar-footer">
              <div className="ds-user-section">
                <div className="ds-user-info">
                  <button className="ds-user-circle" onClick={() => {
                    setUserMenuOpen(!userMenuOpen)
                    if (isMobile) {
                      setMobileSidebarOpen(false)
                    }
                  }}>
                    {getUserAvatar()}
                  </button>
                  <div className="ds-user-details">
                    <div className="ds-user-name">{user?.displayName || "Usuario"}</div>
                    <div className="ds-user-email">{user?.email || ""}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="ds-main-content ds-main-content-with-dashboard">
        {/* Header móvil */}
        <div className="ds-chat-header">
          {isMobile && (
            <button className="ds-mobile-menu-button" onClick={toggleSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
              </svg>
            </button>
          )}
          <div className="ds-chat-header-title">
            <button className="ds-header-option active">
              Recuperación de Clientes
            </button>
          </div>
        </div>

        {/* Contenido del detalle de la tarea */}
        <div style={{ padding: isMobile ? '16px 16px' : '40px', background: '#fbf9f4', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}>
          {/* Botón de volver en desktop */}
          {!isMobile && (
            <button
              onClick={() => navigate("/inversiones")}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '24px',
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '14px',
                color: '#374151',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb'
                e.currentTarget.style.borderColor = '#d1d5db'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
            >
              <ArrowLeft size={18} />
              Volver a Inversiones
            </button>
          )}

          {/* Componente CustomerCards */}
          <CustomerCards />
        </div>
      </div>

      {/* Modal de búsqueda */}
      {searchModalOpen && (
        <>
          {!isMobile && <div className="ds-modal-overlay" onClick={() => { setSearchModalOpen(false); setSearchQuery("") }} />}
          <div 
            className={`ds-search-modal ${isMobile ? "mobile-bottom-sheet" : ""}`}
            style={isMobile ? { transform: `translateY(${searchDragY}px)` } : {}}
          >
            {isMobile && (
              <div 
                className="ds-bottom-sheet-handle"
                onTouchStart={handleSearchTouchStart}
                onTouchMove={handleSearchTouchMove}
                onTouchEnd={handleSearchTouchEnd}
              >
                <div className="ds-handle-bar" />
              </div>
            )}
            <div className="ds-search-modal-header">
              <Search size={20} />
              <input
                type="text"
                placeholder="Buscar en tus chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button onClick={() => { setSearchModalOpen(false); setSearchQuery("") }}>
                <X size={20} />
              </button>
            </div>
            <div className="ds-search-results">
              {debouncedSearchQuery ? (
                filteredHistory.length > 0 ? (
                  filteredHistory.map((chat) => (
                    <div
                      key={chat.id}
                      className="ds-search-result-item"
                      onClick={() => {
                        navigate(`/home?conversation=${chat.id}`)
                        setSearchModalOpen(false)
                        setSearchQuery("")
                      }}
                    >
                      <span className="ds-result-title">{chat.title}</span>
                      <span className="ds-result-date">{formatRelativeDate(chat.date)}</span>
                    </div>
                  ))
                ) : (
                  <div className="ds-no-results">No se encontraron resultados</div>
                )
              ) : (
                <div className="ds-search-hint">Escribe para buscar en tus conversaciones</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de usuario */}
      {userMenuOpen && (
        <>
          {!isMobile && <div className="ds-modal-overlay" onClick={() => setUserMenuOpen(false)} />}
          <div 
            className={`ds-user-modal ${isMobile ? "mobile-bottom-sheet" : ""}`}
            style={isMobile ? { transform: `translateY(${dragY}px)` } : {}}
          >
            {isMobile && (
              <div 
                className="ds-bottom-sheet-handle"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="ds-handle-bar" />
              </div>
            )}

            {modalView === "main" && (
              <>
                <div className="ds-user-modal-header">
                  <div className="ds-user-modal-avatar">{getUserAvatar()}</div>
                  <div className="ds-user-modal-info">
                    <span className="ds-user-modal-name">{user?.displayName || "Usuario"}</span>
                    <span className="ds-user-modal-email">{user?.email}</span>
                  </div>
                </div>

                <div className="ds-user-modal-options">
                  <button onClick={() => setModalView("admin")}>
                    <UserSearch size={18} />
                    <span>Administrar cuenta</span>
                  </button>
                  <button onClick={() => setVoiceModalOpen(true)}>
                    <AudioLines size={18} />
                    <span>Asistente de voz</span>
                  </button>
                  <button
                    className="ds-logout-btn"
                    onClick={async () => {
                      await logout()
                      navigate("/login")
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              </>
            )}

            {modalView === "admin" && (
              <>
                <div className="ds-modal-subheader">
                  <button onClick={() => setModalView("main")}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span>Administrar cuenta</span>
                </div>

                <div className="ds-profile-section">
                  <div className="ds-profile-avatar-section">
                    <div className="ds-profile-avatar-large">{getUserAvatar()}</div>
                    <button className="ds-change-avatar-btn" onClick={handleImageSelect}>
                      Cambiar foto
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </div>

                  <div className="ds-profile-form">
                    <div className="ds-form-group">
                      <label>Nombre</label>
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div className="ds-form-group">
                      <label>Email</label>
                      <input type="email" value={user?.email || ""} disabled />
                    </div>

                    <div className="ds-form-group">
                      <label>Contraseña actual</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Contraseña actual"
                      />
                    </div>

                    <div className="ds-form-group">
                      <label>Nueva contraseña</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nueva contraseña"
                      />
                    </div>

                    <label className="ds-show-password">
                      <input
                        type="checkbox"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                      />
                      Mostrar contraseñas
                    </label>

                    {profileError && <div className="ds-error-msg">{profileError}</div>}
                    {profileSuccess && <div className="ds-success-msg">{profileSuccess}</div>}

                    <button
                      className="ds-save-profile-btn"
                      onClick={handleProfileUpdate}
                      disabled={isUpdatingProfile}
                    >
                      {isUpdatingProfile ? (
                        <>
                          <RefreshCw size={16} className="spinning" />
                          Guardando...
                        </>
                      ) : (
                        "Guardar cambios"
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Modal de Asistente de Voz */}
      <VoiceAssistantModal 
        isOpen={voiceModalOpen} 
        onClose={() => setVoiceModalOpen(false)} 
      />
    </div>
  )
}

export default DetalleTarea
